import {NextRouter, useRouter} from 'next/router';
import {ParsedUrlQueryInput} from 'querystring';
import {useCallback, useMemo, useState} from 'react';
import Url, {qs} from 'url-parse';
import {StaticLandingDayOfWeekMoreFetcher} from '../../../../common/fetchers/StaticLandingDayOfWeekMoreFetcher';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {requestContext} from '../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import useTypedSelector from '../../../../common/useTypedSelector';
import {DayOfWeekTabItemType} from '../../../../design-system/organisms/DayOfWeekTabItem/DayOfWeekTabItemType.interface';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {StaticLandingDayOfWeek as StaticLandingDayOfWeekPage} from '../../../../design-system/pages/StaticLanding/StaticLandingDayOfWeek/StaticLandingDayOfWeek';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {
  DayOfWeekBmType,
  StaticLandingDayOfWeekSection,
  StaticLandingDayOfWeekSectionParam,
} from '../../../../graphql/generated/types';
import {selectIsSubtab} from '../../../../staticLanding/selector';
import {CommonSectionProps} from '../../type';

export function StaticLandingDayOfWeek({
  section,
  children,
}: CommonSectionProps) {
  const isSubtab = useTypedSelector(state => selectIsSubtab({section, state}));
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const StaticLandingDayOfWeekSection = section as Omit<
    StaticLandingDayOfWeekSection,
    'groups'
  >;
  const {
    param,
    totalCount,
    dayTabList: _dayTabList,
    subcategoryList,
    businessModelList: _businessModelList,
    promotionBanner,
  } = StaticLandingDayOfWeekSection;
  const router = useRouter();
  const [isShowSubcategoryDialog, setIsShowSubcategoryDialog] = useState(false);

  const dayTabList: DayOfWeekTabItemType[] = useMemo(() => {
    return _dayTabList.map(({name, param: tabUid}) => {
      const {pathname, query} = getRouteWithParam({
        router,
        param,
        tabUid,
        isSubtab,
      });
      const queryStr = qs.stringify(query);
      return {
        name,
        param: tabUid,
        href: `${pathname}${queryStr ? `?${qs.stringify(query)}` : ''}`,
      };
    });
  }, [_dayTabList, isSubtab, param, router]);

  const businessModelList = useMemo(() => {
    return _businessModelList.map(({name, param: bmType}) => {
      const {pathname, query} = getRouteWithParam({
        router,
        param,
        subcategoryUid: param.subcategory?.param,
        bmType,
        isSubtab,
      });
      const queryStr = qs.stringify(query);
      return {
        name,
        param: bmType,
        href: `${pathname}${queryStr ? `?${qs.stringify(query)}` : ''}`,
      };
    });
  }, [_businessModelList, isSubtab, param, router]);

  const onSelectSubcategoryItem = useCallback(
    (subcategoryUid: string) => {
      setIsShowSubcategoryDialog(false);
      if (subcategoryUid !== param.subcategory?.param) {
        const {pathname, query} = getRouteWithParam({
          router,
          param,
          subcategoryUid,
          bmType: param.businessModel?.param,
          isSubtab,
        });
        router.push(
          {
            pathname,
            query,
          },
          undefined,
          {scroll: false}
        );
      }
    },
    [param, router, isSubtab]
  );

  return (
    <StaticLandingDayOfWeekPage
      isMobile={isMobile}
      isWebView={isWebView}
      isSubtab={isSubtab}
      totalCount={totalCount || 0}
      dayTabList={dayTabList}
      currentDayTabUid={param.dayTab?.param}
      bmList={businessModelList}
      currentBmType={param.businessModel?.param as DayOfWeekBmType}
      subcategoryList={subcategoryList}
      currentSubcategoryUid={param.subcategory?.param}
      isShowSubcategoryDialog={isShowSubcategoryDialog}
      promotionBanner={promotionBanner}
      showSubcategoryDialog={() => setIsShowSubcategoryDialog(true)}
      hideSubcategoryDialog={() => setIsShowSubcategoryDialog(false)}
      onClickSubcategoryItem={onSelectSubcategoryItem}
    >
      {isGreaterThanZero(totalCount) && (
        <StaticLandingDayOfWeekMoreFetcher
          section={StaticLandingDayOfWeekSection}
        >
          {children}
        </StaticLandingDayOfWeekMoreFetcher>
      )}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>{t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}</NoItem>
      )}
    </StaticLandingDayOfWeekPage>
  );
}

function getRouteWithParam({
  param,
  router,
  categoryUid: _categoryUid,
  subcategoryUid,
  bmType,
  tabUid: _tabUid,
  isSubtab,
}: {
  param: StaticLandingDayOfWeekSectionParam;
  router: NextRouter;
  categoryUid?: number;
  subcategoryUid?: string;
  bmType?: string;
  tabUid?: string;
  isSubtab: boolean;
}): {
  pathname: string;
  query: ParsedUrlQueryInput;
} {
  const {categoryUid, dayTab} = param;
  const tabUid = _tabUid ?? dayTab?.param;

  let pathname, query;
  if (isSubtab) {
    // /menu/{menu_uid}/screen/{screen_uid}
    pathname = IS_CLIENT_SIDE
      ? window.location.pathname
      : new Url(router.asPath).pathname;
    // 서브탭인 경우 카테고리 uid를 변경하는 경우는 존재하지 않음
    query = {
      ...(subcategoryUid && {subcategory_uid: subcategoryUid}),
      ...(bmType && {bm: bmType}),
      ...(tabUid && {tab_uid: tabUid}),
    };
  } else {
    pathname = `/landing/day-of-week/${_categoryUid ?? categoryUid}`;
    if (subcategoryUid !== undefined) {
      pathname += `/${subcategoryUid}`;
    }
    query = {
      ...(bmType && {
        bm: bmType,
      }),
      ...(tabUid && {tab_uid: tabUid}),
    };
  }

  return {pathname, query};
}
